import { useNonBrandFilterArgs } from "../use-non-brand-filter-args";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { GridFilterModel, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { useSearchTermsGridQuery } from "src/context/api/non-branded/api";
import { fDate } from "src/utils/format-time";
import { ColumnFeatures } from "./features";
import * as searchTermsColumns from "./search-terms/columns";
import * as competitorsColumns from "./competitors/index";
import { buildRankCell, buildSimpleBarCell, buildSplitCell } from "./search-terms/columns/build";
import useLocales from "src/locales/use-locales";
import { LightTooltip } from "./tooltips/light";
import { Box, Link, Stack, Typography } from "@mui/material";
import { useMemo, useRef } from "react";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import DiffIcon from "../diff-icon";
import { InlineEvolution } from "./inline-evolution";
import { isNegativeDiffGood } from "./search-terms/columns/diff";

/** Width of the inline evolution (56px) + its gap, added to the min width of columns that show it. */
const EVOLUTION_WIDTH = 64;

/** Reads `a.b.c` paths, so split columns can name a scalar nested in their struct. */
const readPath = (obj: any, path: string) => path.split('.').reduce((acc, key) => acc?.[key], obj);

const getResponsiveColumnSize = (column: any) => {
    const features = column.features || [];
    const hasFeature = (feature: ColumnFeatures) => features.includes(feature);

    if (column.field === 'search_term') {
        return { minWidth: 195, flex: 1.9 };
    }

    if (column.field === 'opportunity') {
        return { minWidth: 120, flex: 1.2 };
    }

    if (hasFeature(ColumnFeatures.SPLIT)) {
        return { minWidth: 170, flex: 1.8 };
    }

    if (hasFeature(ColumnFeatures.SIMPLEBAR)) {
        return { minWidth: 150, flex: 1.4 };
    }

    if (hasFeature(ColumnFeatures.RANK)) {
        return { minWidth: 120, flex: 1.15 };
    }

    return { minWidth: 80, flex: 0.9 };
};

export const useSearchTermsGridData = (
    paginationModel: GridPaginationModel & GridFilterModel,
    sortModel: any,
) => {
    // Header filters + CountryBar selection + clusters, shared with every Non Brand query.
    const filterArgs = useNonBrandFilterArgs();
    // Header opportunity pill: restricts the grid like the KPI cards and charts
    const opportunity = useSelector((state: RootState) => state.filters.nonBrandOpportunity);
    const compare = useSelector((state: RootState) => state.filters.compare);
    const compareStart = useSelector((state: RootState) => state.filters.compareStart);
    const compareEnd = useSelector((state: RootState) => state.filters.compareEnd);
    const {
        data: searchTermsGrid,
        isLoading,
        isError,
        isFetching,
    } = useSearchTermsGridQuery({
        ...filterArgs,
        start_compare: fDate(compareStart, 'yyyy-MM-dd'),
        end_compare: fDate(compareEnd, 'yyyy-MM-dd'),
        limit: paginationModel.pageSize,
        page: paginationModel.page,
        filterModel: paginationModel.items,
        opportunity,
        sort_field: sortModel[0]?.field,
        sort_order: sortModel[0]?.sort as string,
        compare: compare,
    });

    return {
        job_id: searchTermsGrid?.job_id,
        searchTermsGrid,
        isLoading,
        isError,
        isFetching,
    };
};

export const useBuildColumns = (
    source: 'search-terms' | 'competitors',
    { evolutionMode = true }: { evolutionMode?: boolean } = {},
) => {
    const { t, currentLang } = useLocales();
    const locale = currentLang?.value || 'en-US';
    const { currency } = (useCurrentWorkspace() as { currency?: string } | undefined) ?? {};
    const popperRef = useRef<{ rowId: string | null }>({ rowId: null });
    const compare = useSelector((state: RootState) => state.filters.compare);
    // Search terms show the evolution inside the cell instead of extra _compare/_diff columns.
    const inlineCompare = source === 'search-terms' && !!compare;
    // Search terms headers follow the maquette: left-aligned (number columns default to right) and #637381.
    const headerProps = source === 'search-terms' ? { headerAlign: 'left' } : {};
    const headerTitleSx = source === 'search-terms' ? { fontWeight: 600, color: '#637381' } : { fontWeight: 600 };
    const columns_rendered: any[] = [];
    const columns = source === 'search-terms' ? searchTermsColumns : competitorsColumns;
    Object.values(columns).sort((a: any, b: any) => a.position - b.position).forEach((column: any) => {
        const { features, color, headerTooltip, renderCell, isEmpty } = column;
        const responsiveSize = getResponsiveColumnSize(column);
        // Numeric columns with a diff, plus split columns that name the scalar to compare (compareField).
        const hasEvolution = column.type === 'number' && (!!features?.includes(ColumnFeatures.DIFF) || !!column.compareField);
        const showEvolution = inlineCompare && hasEvolution;
        // Search terms columns may set their own size (maquette widths); the others keep the responsive one.
        const minWidth = (column.minWidth ?? Math.max(column.width || 0, responsiveSize.minWidth)) + (showEvolution ? EVOLUTION_WIDTH : 0);
        const flex = column.flex ?? responsiveSize.flex;

        const render = useMemo(() => {
            let base: ((params: any) => any) | undefined;
            if (renderCell) base = renderCell;
            else if (features?.includes(ColumnFeatures.SIMPLEBAR)) base = buildSimpleBarCell({ ...column, color });
            else if (features?.includes(ColumnFeatures.RANK)) base = buildRankCell(column, popperRef);
            else if (features?.includes(ColumnFeatures.SPLIT)) base = buildSplitCell(column, popperRef, { breakdown: source === 'search-terms' });
            if (!base || !isEmpty) return base;
            // Null means "no data" and renders an empty cell; 0 still goes through the regular renderer.
            // Renderers of `isEmpty` columns must not call hooks (the early return would change their count).
            const renderBase = base;
            return (params: any) => (isEmpty(params.row) ? null : renderBase(params));
        }, [renderCell, features, color, isEmpty]);

        const renderCompare = useMemo(() => (params: any) => {
            if (!render) return undefined;
            const row = Object.assign({}, params.row, params.row.compare);
            const params_ = Object.assign({}, params, { row });
            return render(params_);
        }, [render]);

        // Always called (constant hook count); only used when the evolution is shown.
        const renderWithEvolution = useMemo(() => (params: any) => {
            const cell = render ? render(params) : params.formattedValue ?? params.value;
            // Empty cell (null value): no evolution either.
            if (cell === null || cell === undefined) return cell;
            const path = column.compareField ?? column.field;
            return (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', height: '100%' }}>
                    {/* Left-aligned: number cells inherit MUI's right alignment, which pushed the value to the right
                        whenever there is no evolution next to it */}
                    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>{cell}</Box>
                    <InlineEvolution
                        value={readPath(params.row, path)}
                        compare={readPath(params.row.compare, path)}
                        kind={column.compareKind ?? 'number'}
                        inverted={column.diff?.good === isNegativeDiffGood}
                        evolutionMode={evolutionMode}
                        currency={currency}
                        locale={locale}
                    />
                </Stack>
            );
        }, [render, evolutionMode, currency, locale]);

        const renderDiff = useMemo(() => (params: any) => {
            const isGood = column.diff?.good?.(column.field)(params);
            return (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    py={1}
                    sx={{ width: '100%', height: '52px' }}
                >
                    <DiffIcon
                        plus={params.row.diff[column.field] > 0}
                        minus={params.row.diff[column.field] < 0}
                        good={isGood}
                        bad={!isGood}
                        evolution={params.row.diff[column.field]}
                        format={column.format}
                    />
                </Stack>
            )
        }, [render]);

        const baseColumn = [{
            ...column,
            ...headerProps,
            renderCell: showEvolution ? renderWithEvolution : render,
            headerName: t(column.headerName),
            minWidth,
            flex,
            renderHeader() {
                if (column.headerTooltipLink) {
                    // Interactive tooltip so the "Learn more." link can be clicked.
                    return (
                        <LightTooltip
                            disableInteractive={false}
                            title={
                                <Typography variant="caption">
                                    {t(headerTooltip)}{' '}
                                    <Link
                                        href={t(column.headerTooltipLink)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="caption"
                                    >
                                        {t(column.headerTooltipLinkLabel)}
                                    </Link>
                                </Typography>
                            }
                        >
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Typography variant="body2" noWrap sx={headerTitleSx}>
                                    {t(column.headerName)}
                                </Typography>
                            </Stack>
                        </LightTooltip>
                    );
                }
                return (
                    <LightTooltip title={t(headerTooltip)}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography variant="body2" noWrap sx={headerTitleSx}>
                                {t(column.headerName)}
                            </Typography>
                        </Stack>
                    </LightTooltip>
                );
            },
        }]

        if (compare && !inlineCompare && column.features?.includes(ColumnFeatures.COMPARISON)) {
            baseColumn.push({
                ...column,
                renderCell: renderCompare,
                headerName: t(column.headerName) + ' (' + t('dashboard.nb_brand_grid.columns.title.compare') + ')',
                field: column.field + '_compare',
                cellClassName: 'column--data-column compare-column',
                minWidth,
                flex,
                renderHeader() {
                    return (
                        <LightTooltip title={t(headerTooltip)}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                {t(column.headerName)} ({t('dashboard.nb_brand_grid.columns.title.compare')})
                            </Typography>
                        </LightTooltip>
                    );
                },
            })
        }
        if (compare && !inlineCompare && column.features?.includes(ColumnFeatures.DIFF)) {
            baseColumn.push({
                ...column,
                field: column.field + '_diff',
                renderCell: renderDiff,
                headerName: t(column.headerName) + ' (' + t('dashboard.nb_brand_grid.columns.title.diff') + ')',
                minWidth,
                flex,
                renderHeader() {
                    return (
                        <LightTooltip title={t(headerTooltip)}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                                {t(column.headerName)} ({t('dashboard.nb_brand_grid.columns.title.diff')})
                            </Typography>
                        </LightTooltip>
                    );
                },
            })
        }

        columns_rendered.push(...baseColumn);
    });
    return columns_rendered;
};